/**
 * 直接嵌入方案（推荐，不使用iframe）
 * 
 * 优势：
 * 1. 通信简单（直接调用函数，不需要postMessage）
 * 2. 样式共享（可以共享CSS和主题）
 * 3. 性能更好（没有额外的DOM层级）
 * 4. 调试方便（统一的DOM树）
 */

import { useEffect, useState, useRef } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import ShareView from './components/ShareView'
import { MarketplaceView } from './components/MarketplaceView'
import { WorksView } from './components/WorksView'
import { SharesManagerView } from './components/SharesManagerView'
import { UserView } from './components/UserView'
import { SettingsView } from './components/SettingsView'
import { HelpView } from './components/HelpView'
import { LibraryView } from './components/LibraryView'
import Layout from './components/Layout'
import ImageViewerDialog from './components/UI/ImageViewerDialog'
import TextViewerDialog from './components/UI/TextViewerDialog'
import FileViewerDialog from './components/UI/FileViewerDialog'
import AllShareLinksDialog from './components/UI/AllShareLinksDialog'
import SystemIntroView from './components/SystemIntroView'
import InitializationView from './components/InitializationView'
import { LinkDialog } from './components/UI/LinkDialog'
import { ImageContextMenu } from './components/UI/ImageContextMenu'
import { ConfirmDialog } from './components/UI/ConfirmDialog'
import { ProjectUploadDialog } from './components/UI/ProjectUploadDialog'
import { htmlBody } from './original/html-body'
import { injectBridgeFunctions } from './original/scripts/bridge'
import { i18nLegacy } from './original/i18n-legacy'
import { useDataStore } from './store/useDataStore'
import { useStorageConfigStore } from './store/useStorageConfigStore'
import { useSettingsStore } from './store/useSettingsStore'
import { checkMainDBExists, checkUserDBExists } from './services/dbManager'
import { getCurrentUserUuid } from './services/userManager'
import './styles/globals.css'
import './styles/original.css'

// 在 Tauri 环境中预加载 API 模块
if (typeof window !== 'undefined' && (window as any).__TAURI__) {
  import('@tauri-apps/api/dialog').then(module => {
    const tauri = (window as any).__TAURI__
    if (tauri && !tauri.dialog) {
      tauri.dialog = module
      console.log('✅ Tauri dialog 模块已预加载')
    }
  }).catch(err => {
    console.warn('⚠️ 预加载 dialog 模块失败:', err)
  })
  
  import('@tauri-apps/api/fs').then(module => {
    const tauri = (window as any).__TAURI__
    if (tauri && !tauri.fs) {
      tauri.fs = module
      console.log('✅ Tauri fs 模块已预加载')
    }
  }).catch(err => {
    console.warn('⚠️ 预加载 fs 模块失败:', err)
  })
  
  import('@tauri-apps/api/shell').then(module => {
    const tauri = (window as any).__TAURI__
    if (tauri && !tauri.shell) {
      tauri.shell = module
      console.log('✅ Tauri shell 模块已预加载')
    }
  }).catch(err => {
    console.warn('⚠️ 预加载 shell 模块失败:', err)
  })
  
  // 预加载 tauri invoke API（用于调用自定义命令）
  import('@tauri-apps/api/tauri').then(module => {
    const tauri = (window as any).__TAURI__
    if (tauri && !tauri.invoke) {
      tauri.invoke = module.invoke
      console.log('✅ Tauri invoke API 已预加载')
    }
    // 同时设置到全局，方便 bridge.ts 访问
    ;(window as any).__TAURI_INVOKE__ = module.invoke
  }).catch(err => {
    console.warn('⚠️ 预加载 tauri invoke API 失败:', err)
  })
}

// 全局标志，防止多次初始化（即使在 React Strict Mode 下）
let globalInitialized = false

// 将 useDataStore 暴露到 window 对象，供原生代码访问
;(window as any).useDataStore = useDataStore

// 主应用组件（非分享模式）
const MainApp = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const initStartedRef = useRef(false) // 防止同一实例多次初始化
  const location = useLocation()
  const { initConfig } = useStorageConfigStore()
  const { initData } = useDataStore()
  const { loadSettings } = useSettingsStore()
  
  // 从 URL hash 或 pathname 获取当前 tab，如果没有则默认为 'create'
  const getInitialTab = () => {
    // 检查 URL hash（如 #marketplace）
    if (location.hash) {
      const hashTab = location.hash.replace('#', '')
      if (hashTab && ['marketplace', 'works', 'create', 'share', 'help', 'settings', 'intro', 'mine'].includes(hashTab)) {
        return hashTab
      }
    }
    // 检查 pathname（如 /marketplace）
    const pathname = location.pathname
    if (pathname && pathname !== '/' && pathname !== '/share') {
      const pathTab = pathname.replace('/', '')
      if (pathTab && ['marketplace', 'works', 'create', 'share', 'help', 'settings', 'intro', 'mine'].includes(pathTab)) {
        return pathTab
      }
    }
    return 'create'
  }
  
  const [activeTab, setActiveTab] = useState(getInitialTab())

  // 当 URL 变化时，同步更新 activeTab
  useEffect(() => {
    const newTab = getInitialTab()
    if (newTab !== activeTab) {
      setActiveTab(newTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.pathname])

  const handleTabChange = (tab: string) => {
    // 如果从创作页面切换到其他页面，关闭属性面板
    if (activeTab === 'create' && tab !== 'create') {
      const win = window as any
      // 关闭通用属性面板
      if (typeof win.hidePropertiesPanel === 'function') {
        win.hidePropertiesPanel()
      }
      // 关闭画笔属性面板
      if (typeof win.hideDrawingPropertiesPanel === 'function') {
        win.hideDrawingPropertiesPanel()
      }
    }
    
    setActiveTab(tab)
    // 更新 URL hash，但不触发页面刷新
    window.history.replaceState(null, '', `#${tab}`)
  }

  const handleOpenProject = async (projectUuid: string) => {
    const win = window as any
    const { setCurrentProject } = useDataStore.getState()
    setCurrentProject(projectUuid)
    
    // 先切换到 create tab，确保 DOM 已显示
    setActiveTab('create')
    
    // 等待 DOM 更新完成，确保视口尺寸正确
    await new Promise(resolve => {
      // 使用 requestAnimationFrame 确保 DOM 已更新
      requestAnimationFrame(() => {
        // 再等待一个 frame 确保视口尺寸已计算
        requestAnimationFrame(() => {
          resolve(undefined)
        })
      })
    })
    
    // 触发原生代码重新加载项目
    if (typeof win.loadProject === 'function') {
      await win.loadProject(projectUuid)
    } else if (typeof win.init === 'function') {
      // 重新初始化以加载新项目
      win.init()
    }
  }
  
  // 统一的查看器状态（与分享页面一致）
  const [viewerState, setViewerState] = useState<{
    type: 'image' | 'text' | 'file' | null
    currentIndex: number
    itemIndex: number // 原始项目索引
  }>({
    type: null,
    currentIndex: -1,
    itemIndex: -1
  })
  
  // 存储当前项目的所有items（用于查看器）
  const [currentProjectItems, setCurrentProjectItems] = useState<any[]>([])

  // 右键菜单状态
  const [contextMenuState, setContextMenuState] = useState<{
    visible: boolean
    itemIndex: number
    x: number
    y: number
  }>({
    visible: false,
    itemIndex: -1,
    x: 0,
    y: 0
  })

  // 链接弹窗状态
  const [linkDialogState, setLinkDialogState] = useState<{
    visible: boolean
    itemIndex: number
    initialUrl: string
  }>({
    visible: false,
    itemIndex: -1,
    initialUrl: ''
  })

  // 所有分享链接弹窗状态
  const [showAllShareLinksDialog, setShowAllShareLinksDialog] = useState(false)

  // 关闭项目确认对话框状态
  const [closeProjectConfirm, setCloseProjectConfirm] = useState<{
    visible: boolean
    projectId: string
    projectName: string
  }>({
    visible: false,
    projectId: '',
    projectName: ''
  })

  // 项目上传对话框状态
  const [showProjectUploadDialog, setShowProjectUploadDialog] = useState(false)

  // 初始化存储配置
  useEffect(() => {
    console.log('📦 App.tsx: 开始初始化存储配置...')
    initConfig()
      .then(() => {
        console.log('📦 App.tsx: 存储配置初始化成功')
      })
      .catch(err => {
        console.error('❌ App.tsx: 初始化存储配置失败:', err)
      })
  }, [initConfig])

  // 初始化设置
  useEffect(() => {
    console.log('📦 App.tsx: 开始初始化设置...')
    loadSettings()
      .then(() => {
        console.log('📦 App.tsx: 设置初始化完成')
      })
      .catch(err => {
        console.error('❌ App.tsx: 初始化设置失败:', err)
      })
  }, [loadSettings])

  // 初始化数据（包括 userUuid）
  useEffect(() => {
    console.log('📦 App.tsx: 开始初始化数据...')
    initData()
      .then(() => {
        console.log('📦 App.tsx: 数据初始化成功')
      })
      .catch(err => {
        console.error('❌ App.tsx: 初始化数据失败:', err)
      })
  }, [initData])

  // 监听 userUuid 变化，切换用户时重新加载原生代码的项目和设置
  const { userUuid } = useDataStore()
  
  useEffect(() => {
    if (!userUuid) return
    
    console.log('[App] userUuid 变化，开始刷新数据:', userUuid)
    
    // 切换用户时重新加载设置
    loadSettings()
      .then(() => {
        console.log('📦 App.tsx: 用户切换后设置已重新加载')
      })
      .catch(err => {
        console.error('❌ App.tsx: 用户切换后加载设置失败:', err)
      })
    
    // 注意：原生代码的数据同步已经在 useDataStore.setUserUuid 中处理
    // 这里只需要确保设置被重新加载
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUuid, loadSettings])

  // 监听原生代码的数据保存事件，同步更新 React store
  useEffect(() => {
    const handleDataSaved = async (event: CustomEvent) => {
      console.log('[App] 收到 dataSaved 事件')
      
      // 获取当前的 userUuid
      const { userUuid: currentUserUuid } = useDataStore.getState()
      
      // 如果事件中有 userUuid，检查是否是当前用户
      const eventUserUuid = event.detail?.userUuid
      if (eventUserUuid && eventUserUuid !== currentUserUuid) {
        console.log('[App] dataSaved 事件来自其他用户，忽略')
        return
      }
      
      // 从原生代码的 data 对象同步数据，而不是重新从数据库加载
      const win = window as any
      if (win.data && win.data.projects) {
        // 始终同步数据（项目名称、items 等属性变化也需要同步）
        console.log('[App] 从原生代码同步数据，项目数:', win.data.projects.length)
        useDataStore.setState({
          data: {
            projects: win.data.projects,
            currentProjectId: win.data.currentProjectId,
            userUuid: win.data.userUuid || currentUserUuid
          }
        })
      }
    }

    window.addEventListener('dataSaved', handleDataSaved as unknown as EventListener)
    return () => {
      window.removeEventListener('dataSaved', handleDataSaved as unknown as EventListener)
    }
  }, [])

  // 监听右键菜单事件
  useEffect(() => {
    const handleShowImageContextMenu = (e: CustomEvent) => {
      const { itemIndex, x, y } = e.detail
      setContextMenuState({
        visible: true,
        itemIndex,
        x,
        y
      })
    }

    window.addEventListener('showImageContextMenu', handleShowImageContextMenu as EventListener)

    return () => {
      window.removeEventListener('showImageContextMenu', handleShowImageContextMenu as EventListener)
    }
  }, [])

  // 监听显示所有分享链接弹窗事件
  useEffect(() => {
    const handleShowAllShareLinks = () => {
      setShowAllShareLinksDialog(true)
    }

    const handleShowLinkDialog = (e: CustomEvent) => {
        setLinkDialogState({
            visible: true,
            itemIndex: e.detail.itemIndex,
            initialUrl: e.detail.initialUrl || ''
        })
    }

    // 注册全局函数供原生代码调用
    ;(window as any).showAllShareLinksDialog = handleShowAllShareLinks
    ;(window as any).showLinkDialog = (itemIndex: number, initialUrl: string) => {
        window.dispatchEvent(new CustomEvent('showLinkDialog', { detail: { itemIndex, initialUrl } }))
    }

    window.addEventListener('showLinkDialog', handleShowLinkDialog as EventListener)

    return () => {
      delete (window as any).showAllShareLinksDialog
      delete (window as any).showLinkDialog
      window.removeEventListener('showLinkDialog', handleShowLinkDialog as EventListener)
    }
  }, [])

  useEffect(() => {
    // 如果已经初始化过了（全局检查），直接返回
    if (globalInitialized) {
      console.log('⚠️ 应用已全局初始化，跳过')
      return
    }

    // 如果当前实例已经开始初始化，直接返回
    if (initStartedRef.current) {
      console.log('⚠️ 当前实例已开始初始化，跳过')
      return
    }

    // 标记当前实例已开始初始化
    initStartedRef.current = true

    let scriptElement: HTMLScriptElement | null = null

    const initializeApp = async () => {
      // 首先注入 i18n 对象（必须在脚本加载之前）
      console.log('🌐 开始注入 i18n 对象...')
      try {
        ;(window as any).i18n = i18nLegacy
        const LANG_KEY = 'app_language'
        ;(window as any).LANG_KEY = LANG_KEY
        ;(window as any).currentLang = localStorage.getItem(LANG_KEY) || 'zh-CN'
        console.log('✅ i18n 对象注入完成')
      } catch (err) {
        console.error('❌ 注入 i18n 对象失败:', err)
      }
      
      // 然后注入桥接函数（等待完成）
      console.log('🔧 开始注入桥接函数...')
      try {
        await injectBridgeFunctions()
        console.log('✅ 桥接函数注入完成')
      } catch (err) {
        console.error('❌ 注入桥接函数失败:', err)
      }
      console.log('🚀 开始初始化应用...')
      
      // 等待 containerRef 准备好
      let retryCount = 0
      while (!containerRef.current && retryCount < 10) {
        console.log(`⏳ 等待容器准备就绪... (${retryCount + 1}/10)`)
        await new Promise(resolve => setTimeout(resolve, 50))
        retryCount++
      }
      
      if (!containerRef.current) {
        console.error('❌ 容器ref未准备好，无法继续初始化')
        initStartedRef.current = false
        return
      }
      
      console.log('✅ 容器ref已准备好')
      
      try {
        // 1. 嵌入HTML body内容
        console.log('📝 步骤1: 嵌入HTML body内容')
        if (!containerRef.current) {
          console.error('❌ 容器ref未准备好')
          return
        }
        containerRef.current.innerHTML = htmlBody
        console.log('✅ HTML body内容已嵌入，容器内容长度:', containerRef.current.innerHTML.length)

        // 2. 动态加载原HTML的JavaScript代码
        console.log('📥 步骤2: 开始加载 infinitenote-api.html')
        let htmlText = ''
        
        try {
          // 在 Tauri 桌面版中使用相对路径，Web 版也兼容
          const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined
          const htmlPath = isTauri ? './infinitenote-api.html' : '/infinitenote-api.html'
          const response = await fetch(htmlPath)
          if (!response.ok) {
            throw new Error(`HTTP错误: ${response.status} ${response.statusText}`)
          }
          htmlText = await response.text()
          console.log(`✅ HTML文件加载成功 (${(htmlText.length / 1024).toFixed(2)} KB)`)
        } catch (fetchError) {
          console.error('❌ 加载HTML文件失败:', fetchError)
          initStartedRef.current = false
          return
        }
        
        // 加载外部脚本 legacy-script.js
        console.log('🔍 步骤3: 加载外部脚本 legacy-script.js')
        try {
          if (!containerRef.current) {
            console.error('❌ containerRef.current 不存在，无法加载脚本')
            initStartedRef.current = false
            return
          }
          
          // 在 Tauri 桌面版中使用相对路径，Web 版也兼容
          const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined
          // 添加时间戳防止缓存
          const timestamp = new Date().getTime()
          const scriptPath = isTauri ? `./legacy-script.js?v=${timestamp}_v2` : `/legacy-script.js?v=${timestamp}_v2`
          
          // 创建 script 元素并加载外部脚本
          scriptElement = document.createElement('script')
          scriptElement.src = scriptPath
          scriptElement.async = false // 确保按顺序执行
          
          // 等待脚本加载完成
          await new Promise<void>((resolve, reject) => {
            scriptElement!.onload = () => {
              console.log('✅ legacy-script.js 加载完成')
              resolve()
            }
            scriptElement!.onerror = (error) => {
              console.error('❌ legacy-script.js 加载失败:', error)
              reject(error)
            }
            containerRef.current!.appendChild(scriptElement!)
          })
          
          // 等待脚本执行完成
          await new Promise(resolve => setTimeout(resolve, 200))
          
          console.log(`✅ 原HTML脚本已加载并执行`)
        } catch (scriptError) {
          console.error('❌ Script加载失败:', scriptError)
          initStartedRef.current = false
          return
        }

        // 3. 确保DOM完全准备好后再初始化
        console.log('🎯 步骤4: 等待DOM准备并初始化')
        
        // 等待DOM完全渲染（增加等待时间确保脚本完全执行）
        await new Promise(resolve => setTimeout(resolve, 300))
        
        // 确保容器内的所有元素都已渲染
        if (containerRef.current) {
          // 强制浏览器重新计算布局
          containerRef.current.offsetHeight
          
          // 等待多个事件循环，确保所有DOM操作和脚本执行完成
          await new Promise(resolve => requestAnimationFrame(() => 
            requestAnimationFrame(() => 
              requestAnimationFrame(resolve)
            )
          ))
        }
        
        // 检查init函数是否已经存在（脚本执行后应该存在）
        let initFunctionExists = typeof (window as any).init === 'function'
        console.log(`🔍 init函数存在: ${initFunctionExists}`)
        
        // 如果init函数还不存在，等待一下再检查（脚本可能还在执行）
        if (!initFunctionExists) {
          console.log('⏳ init函数尚未存在，等待脚本执行...')
          for (let i = 0; i < 10; i++) {
            await new Promise(resolve => setTimeout(resolve, 100))
            if (typeof (window as any).init === 'function') {
              initFunctionExists = true
              console.log(`✅ init函数在第${i + 1}次检查时出现`)
              break
            }
          }
        }
        
        // 先触发DOMContentLoaded事件（原始代码依赖这个事件）
        // 这样可以确保原始代码中的事件监听器能够正确执行
        console.log('📢 触发 DOMContentLoaded 事件')
        const domContentLoadedEvent = new Event('DOMContentLoaded', { bubbles: true, cancelable: true })
        window.dispatchEvent(domContentLoadedEvent)
        document.dispatchEvent(domContentLoadedEvent)
        if (containerRef.current) {
          containerRef.current.dispatchEvent(domContentLoadedEvent)
        }
        
        // 等待事件监听器执行
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // 如果init函数存在，也直接调用（双重保险）
        if (typeof (window as any).init === 'function') {
          console.log('✅ 找到 init 函数，直接调用')
          try {
            await (window as any).init()
            console.log('✅ init 函数执行完成')
          } catch (initError) {
            console.error('❌ init 函数执行失败:', initError)
          }
        } else {
          console.warn('⚠️ 未找到 init 函数，但已触发 DOMContentLoaded 事件')
          console.warn('⚠️ 可能脚本执行有问题，请检查控制台错误')
        }
        
        // 标记为已全局初始化
        globalInitialized = true

        console.log('✅ 初始化流程完成')

      } catch (error) {
        console.error('❌ 初始化失败:', error)
        initStartedRef.current = false
      }
    }

    initializeApp()
  }, [])

  // 单独设置事件监听器，确保它总是被添加
  useEffect(() => {
    console.log('🎧 设置事件监听器')
    
    const handleCustomEvent = (event: CustomEvent) => {
      console.log('📥 收到事件:', event.type, event.detail)
      if (event.type === 'openItemViewer') {
        const itemIndex = event.detail?.itemIndex ?? -1
        console.log('🔍 处理 openItemViewer 事件，itemIndex:', itemIndex)
        if (itemIndex >= 0) {
          // 获取当前项目数据
          const win = window as any
          if (typeof win.getCurrentProject === 'function') {
            const project = win.getCurrentProject()
            console.log('📦 获取到项目:', project ? `项目有 ${project.items?.length || 0} 个items` : 'null')
            if (project && project.items && project.items.length > 0) {
              setCurrentProjectItems(project.items)
              
              // 找到可查看的items索引（排除欢迎文本）
              const viewableItems: number[] = project.items
                .map((item: any, index: number) => ({ item, index }))
                .filter(({ item }: any) => {
                  // 排除欢迎文本
                  if (item.type === 'text' && item.isWelcomeText) {
                    return false
                  }
                  return true
                })
                .map(({ index }: any) => index)
              
              console.log('📋 可查看的items索引:', viewableItems)
              const viewableIndex = viewableItems.indexOf(itemIndex)
              console.log('🎯 找到的viewableIndex:', viewableIndex)
              if (viewableIndex !== -1) {
                const item = project.items[itemIndex]
                console.log('✅ 打开查看器，类型:', item.type)
                setViewerState({
                  type: item.type,
                  currentIndex: viewableIndex,
                  itemIndex: itemIndex
                })
              } else {
                console.warn('⚠️ itemIndex 不在可查看列表中')
              }
            } else {
              console.warn('⚠️ 项目或items为空')
            }
          } else {
            console.error('❌ getCurrentProject 函数不存在')
          }
        } else {
          console.warn('⚠️ itemIndex 无效:', itemIndex)
        }
      }
    }

    window.addEventListener('openItemViewer', handleCustomEvent as EventListener)
    console.log('✅ 事件监听器已添加')

    // 监听关闭项目确认事件
    const handleCloseProjectConfirm = (event: CustomEvent) => {
      const { projectId, projectName } = event.detail || {}
      if (projectId) {
        setCloseProjectConfirm({
          visible: true,
          projectId,
          projectName: projectName || '未命名画布'
        })
      }
    }
    window.addEventListener('showCloseProjectConfirm', handleCloseProjectConfirm as EventListener)

    const handleShowProjectUploadDialog = () => {
      console.log('🎯 [App.tsx] 收到 showProjectUploadDialog 事件')
      const { data } = useDataStore.getState()
      console.log('📊 [App.tsx] Store状态:', { 
          currentProjectId: data.currentProjectId, 
          projectsCount: data.projects.length 
      })
      setShowProjectUploadDialog(true)
    }
    window.addEventListener('showProjectUploadDialog', handleShowProjectUploadDialog as EventListener)
    console.log('✅ [App.tsx] showProjectUploadDialog 事件监听器已添加')

    return () => {
      console.log('🧹 移除事件监听器')
      window.removeEventListener('openItemViewer', handleCustomEvent as EventListener)
      window.removeEventListener('showCloseProjectConfirm', handleCloseProjectConfirm as EventListener)
      window.removeEventListener('showProjectUploadDialog', handleShowProjectUploadDialog as EventListener)
    }
  }, [])

  // 获取所有可查看的项目索引
  const getViewableItems = (): number[] => {
    return currentProjectItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        if (item.type === 'text' && item.isWelcomeText) {
          return false
        }
        return true
      })
      .map(({ index }) => index)
  }

  const viewableItems = getViewableItems()

  // 获取当前查看的项目
  const getCurrentViewItem = () => {
    if (viewerState.type === null || viewerState.currentIndex < 0) return null
    const itemIndex = viewableItems[viewerState.currentIndex]
    if (itemIndex === undefined || !currentProjectItems[itemIndex]) return null
    return currentProjectItems[itemIndex]
  }

  const handleCloseViewer = () => {
    setViewerState({
      type: null,
      currentIndex: -1,
      itemIndex: -1
    })
  }

  const handlePrevViewer = () => {
    if (viewerState.currentIndex > 0) {
      const newIndex = viewerState.currentIndex - 1
      const itemIndex = viewableItems[newIndex]
      const item = currentProjectItems[itemIndex]
      if (item) {
        setViewerState({
          type: item.type,
          currentIndex: newIndex,
          itemIndex: itemIndex
        })
      }
    }
  }

  const handleNextViewer = () => {
    if (viewerState.currentIndex < viewableItems.length - 1) {
      const newIndex = viewerState.currentIndex + 1
      const itemIndex = viewableItems[newIndex]
      const item = currentProjectItems[itemIndex]
      if (item) {
        setViewerState({
          type: item.type,
          currentIndex: newIndex,
          itemIndex: itemIndex
        })
      }
    }
  }

  return (
    <Layout activeTab={activeTab} onTabChange={handleTabChange}>
      {/* 创作 (Original Canvas) - 始终渲染但控制可见性 */}
      <div 
        className="w-full h-full" 
        style={{ display: activeTab === 'create' ? 'block' : 'none' }}
      >
        <div className="w-full h-full overflow-hidden relative" style={{ backgroundColor: 'transparent' }}>
          {/* 原HTML应用容器 - 始终渲染，确保ref可用 */}
          <div 
            ref={containerRef}
            id="original-app-container"
            className="w-full h-full"
            style={{ 
              backgroundColor: 'transparent',
              position: 'relative',
              pointerEvents: 'auto' // 确保可以接收鼠标事件
            }}
            onContextMenu={() => {
              // 不阻止原生代码的右键菜单
              // 让事件继续传播到原生代码处理
            }}
          />
          
          {/* 统一的查看器（与分享页面一致） */}
      {viewerState.type && (() => {
        const currentItem = getCurrentViewItem()
        if (!currentItem) return null

        const currentIndex = viewerState.currentIndex
        const totalCount = viewableItems.length
        const hasPrev = currentIndex > 0
        const hasNext = currentIndex < totalCount - 1

        // 通用的旋转保存回调函数
        const handleRotationChange = async (rotation: number) => {
          const win = window as any
          const itemIndex = viewerState.itemIndex
          
          console.log('[handleRotationChange] 保存旋转角度:', rotation, 'itemIndex:', itemIndex)
          
          if (typeof win.getCurrentProject !== 'function') return
          
          const currentProject = win.getCurrentProject()
          if (!currentProject || !currentProject.items || itemIndex < 0) return
          
          const item = currentProject.items[itemIndex]
          if (!item) return
          
          // 获取旧的旋转角度
          const oldRotation = item.rotation || 0
          
          // 判断是否需要交换宽高
          // 当从 0/180 变为 90/270，或从 90/270 变为 0/180 时需要交换
          const oldIsVertical = (oldRotation === 90 || oldRotation === 270)
          const newIsVertical = (rotation === 90 || rotation === 270)
          
          if (oldIsVertical !== newIsVertical && item.width && item.height) {
            // 交换宽高
            const tempWidth = item.width
            item.width = item.height
            item.height = tempWidth
            
            // 如果是图片类型，也交换图片尺寸
            if (item.type === 'image' && item.imageWidth && item.imageHeight) {
              const tempImgWidth = item.imageWidth
              item.imageWidth = item.imageHeight
              item.imageHeight = tempImgWidth
            }
            
            console.log('[handleRotationChange] 交换宽高:', { width: item.width, height: item.height })
          }
          
          // 更新旋转信息
          item.rotation = rotation
          item.updatedAt = new Date().toISOString()
          
          // 更新画布显示
          if (typeof win.renderCanvas === 'function') {
            win.renderCanvas()
          }
          
          // 保存到IndexedDB
          if (typeof win.saveData === 'function') {
            try {
              await win.saveData()
            } catch (e) {
              console.error('[handleRotationChange] 保存失败:', e)
            }
          }
          
          // 更新本地状态
          setCurrentProjectItems([...currentProject.items])
        }

        if (viewerState.type === 'image') {
          // 处理分享链接变化
          const handleShareLinkChange = async (shareLink: string) => {
            const win = window as any
            const itemIndex = viewerState.itemIndex
            
            console.log('[handleShareLinkChange] 开始保存，itemIndex:', itemIndex, 'shareLink:', shareLink)
            
            // 直接从原HTML代码获取当前项目
            if (typeof win.getCurrentProject !== 'function') {
              console.error('[handleShareLinkChange] getCurrentProject函数不存在')
              return
            }
            
            const currentProject = win.getCurrentProject()
            if (!currentProject || !currentProject.items || itemIndex < 0) {
              console.error('[handleShareLinkChange] 项目不存在或itemIndex无效')
              return
            }
            
            const item = currentProject.items[itemIndex]
            if (!item || item.type !== 'image') {
              console.error('[handleShareLinkChange] item不存在或不是图片类型')
              return
            }
            
            // 更新分享链接
            item.shareLink = shareLink || undefined
            item.updatedAt = new Date().toISOString()
            
            console.log('[handleShareLinkChange] 已更新item shareLink')
            
            // 更新画布显示
            if (typeof win.renderCanvas === 'function') {
              win.renderCanvas()
            }
            
            // 保存到IndexedDB
            if (typeof win.saveData === 'function') {
              try {
                await win.saveData()
                console.log('[handleShareLinkChange] 数据已保存到IndexedDB')
              } catch (e) {
                console.error('[handleShareLinkChange] 保存到IndexedDB失败:', e)
              }
            }
            
            // 更新本地状态
            setCurrentProjectItems([...currentProject.items])
          }

          // 使用 ImageUrlLoader 组件来异步加载图片
          return (
            <ImageUrlLoader
              item={currentItem}
              onLoad={(imageUrl) => (
                <ImageViewerDialog
                  imageUrl={imageUrl}
                  fileName={currentItem.fileId || currentItem.imageId ? `image-${currentItem.fileId || currentItem.imageId}.png` : 'image.png'}
                  onClose={handleCloseViewer}
                  onPrev={hasPrev ? handlePrevViewer : undefined}
                  onNext={hasNext ? handleNextViewer : undefined}
                  hasPrev={hasPrev}
                  hasNext={hasNext}
                  currentIndex={currentIndex}
                  totalCount={totalCount}
                  initialRotation={currentItem.rotation || 0}
                  onRotationChange={handleRotationChange}
                  shareLink={currentItem.shareLink || ''}
                  onShareLinkChange={handleShareLinkChange}
                  showShareLinkInput={true}
                />
              )}
            />
          )
        } else if (viewerState.type === 'text') {
          const handleSaveText = async (content: string, fontSize: number, fontFamily: string) => {
            const win = window as any
            const itemIndex = viewerState.itemIndex
            
            console.log('[handleSaveText] 开始保存，itemIndex:', itemIndex, 'content长度:', content.length)
            
            // 直接从原HTML代码获取当前项目（确保数据一致性）
            if (typeof win.getCurrentProject !== 'function') {
              console.error('[handleSaveText] getCurrentProject函数不存在')
              return
            }
            
            const currentProject = win.getCurrentProject()
            if (!currentProject || !currentProject.items || itemIndex < 0) {
              console.error('[handleSaveText] 项目不存在或itemIndex无效')
              return
            }
            
            const item = currentProject.items[itemIndex]
            if (!item || item.type !== 'text') {
              console.error('[handleSaveText] item不存在或不是文本类型')
              return
            }
            
            // 直接更新原HTML代码中的item数据
            item.content = content
            item.fontSize = fontSize
            item.fontFamily = fontFamily
            item.updatedAt = new Date().toISOString()
            
            console.log('[handleSaveText] 已更新item:', {
              id: item.id,
              content: content.substring(0, 50),
              fontSize,
              fontFamily
            })
            
            // 更新画布显示
            if (typeof win.renderCanvas === 'function') {
              win.renderCanvas()
            }
            
            // 保存到IndexedDB
            if (typeof win.saveData === 'function') {
              try {
                await win.saveData()
                console.log('[handleSaveText] 数据已保存到IndexedDB')
              } catch (e) {
                console.error('[handleSaveText] 保存到IndexedDB失败:', e)
              }
            } else {
              console.error('[handleSaveText] saveData函数不存在')
            }
            
            // 同时更新React store（保持数据一致性）
            // 但不依赖React store进行保存
            try {
              const { updateProject } = useDataStore.getState()
              const { data } = useDataStore.getState()
              const reactProject = data.projects.find(p => p.id === data.currentProjectId)
              if (reactProject && reactProject.items && reactProject.items[itemIndex]) {
                const updatedItems = [...reactProject.items]
                const existingItem = updatedItems[itemIndex]
                updatedItems[itemIndex] = {
                  ...existingItem,
                  content,
                  fontSize,
                  fontFamily,
                  updatedAt: new Date().toISOString()
                } as any // 类型断言，因为Item类型定义可能不完整
                updateProject(reactProject.id || reactProject.uuid, {
                  items: updatedItems,
                  updatedAt: Date.now()
                })
              }
            } catch (e) {
              console.warn('[handleSaveText] 更新React store失败（不影响主要保存）:', e)
            }
            
            // 更新本地状态以反映变化
            setCurrentProjectItems([...currentProject.items])
          }

          // 处理文本的分享链接变化
          const handleTextShareLinkChange = async (shareLink: string) => {
            const win = window as any
            const itemIndex = viewerState.itemIndex
            
            console.log('[handleTextShareLinkChange] 开始保存，itemIndex:', itemIndex, 'shareLink:', shareLink)
            
            // 直接从原HTML代码获取当前项目
            if (typeof win.getCurrentProject !== 'function') {
              console.error('[handleTextShareLinkChange] getCurrentProject函数不存在')
              return
            }
            
            const currentProject = win.getCurrentProject()
            if (!currentProject || !currentProject.items || itemIndex < 0) {
              console.error('[handleTextShareLinkChange] 项目不存在或itemIndex无效')
              return
            }
            
            const item = currentProject.items[itemIndex]
            if (!item || item.type !== 'text') {
              console.error('[handleTextShareLinkChange] item不存在或不是文本类型')
              return
            }
            
            // 更新分享链接
            item.shareLink = shareLink || undefined
            item.updatedAt = new Date().toISOString()
            
            console.log('[handleTextShareLinkChange] 已更新item shareLink')
            
            // 更新画布显示
            if (typeof win.renderCanvas === 'function') {
              win.renderCanvas()
            }
            
            // 保存到IndexedDB
            if (typeof win.saveData === 'function') {
              try {
                await win.saveData()
                console.log('[handleTextShareLinkChange] 数据已保存到IndexedDB')
              } catch (e) {
                console.error('[handleTextShareLinkChange] 保存到IndexedDB失败:', e)
              }
            }
            
            // 更新本地状态
            setCurrentProjectItems([...currentProject.items])
          }
          
          return (
            <TextViewerDialog
              content={currentItem.content || ''}
              fileName={`text-${currentItem.uuid || currentIndex}.txt`}
              fontSize={currentItem.fontSize || 14}
              fontFamily={currentItem.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif'}
              onClose={handleCloseViewer}
              onPrev={hasPrev ? handlePrevViewer : undefined}
              onNext={hasNext ? handleNextViewer : undefined}
              hasPrev={hasPrev}
              hasNext={hasNext}
              currentIndex={currentIndex}
              totalCount={totalCount}
              onSave={handleSaveText}
              initialRotation={currentItem.rotation || 0}
              onRotationChange={handleRotationChange}
              shareLink={currentItem.shareLink || ''}
              onShareLinkChange={handleTextShareLinkChange}
              showShareLinkInput={true}
            />
          )
        } else if (viewerState.type === 'file') {
          // 处理文件的分享链接变化
          const handleFileShareLinkChange = async (shareLink: string) => {
            const win = window as any
            const itemIndex = viewerState.itemIndex
            
            console.log('[handleFileShareLinkChange] 开始保存，itemIndex:', itemIndex, 'shareLink:', shareLink)
            
            // 直接从原HTML代码获取当前项目
            if (typeof win.getCurrentProject !== 'function') {
              console.error('[handleFileShareLinkChange] getCurrentProject函数不存在')
              return
            }
            
            const currentProject = win.getCurrentProject()
            if (!currentProject || !currentProject.items || itemIndex < 0) {
              console.error('[handleFileShareLinkChange] 项目不存在或itemIndex无效')
              return
            }
            
            const item = currentProject.items[itemIndex]
            if (!item || item.type !== 'file') {
              console.error('[handleFileShareLinkChange] item不存在或不是文件类型')
              return
            }
            
            // 更新分享链接
            item.shareLink = shareLink || undefined
            item.updatedAt = new Date().toISOString()
            
            console.log('[handleFileShareLinkChange] 已更新item shareLink')
            
            // 更新画布显示
            if (typeof win.renderCanvas === 'function') {
              win.renderCanvas()
            }
            
            // 保存到IndexedDB
            if (typeof win.saveData === 'function') {
              try {
                await win.saveData()
                console.log('[handleFileShareLinkChange] 数据已保存到IndexedDB')
              } catch (e) {
                console.error('[handleFileShareLinkChange] 保存到IndexedDB失败:', e)
              }
            }
            
            // 更新本地状态
            setCurrentProjectItems([...currentProject.items])
          }

          // 使用 FileUrlLoader 组件来异步加载文件
          return (
            <FileUrlLoader
              item={currentItem}
              onLoad={(fileUrl) => (
                <FileViewerDialog
                  fileUrl={fileUrl}
                  fileName={currentItem.fileName || currentItem.fileId || 'file'}
                  fileType={currentItem.fileType}
                  localFilePath={currentItem.localFilePath || null}
                  onClose={handleCloseViewer}
                  onPrev={hasPrev ? handlePrevViewer : undefined}
                  onNext={hasNext ? handleNextViewer : undefined}
                  hasPrev={hasPrev}
                  hasNext={hasNext}
                  currentIndex={currentIndex}
                  totalCount={totalCount}
                  initialRotation={currentItem.rotation || 0}
                  onRotationChange={handleRotationChange}
                  shareLink={currentItem.shareLink || ''}
                  onShareLinkChange={handleFileShareLinkChange}
                  showShareLinkInput={true}
                />
              )}
            />
          )
        }

        return null
      })()}

      {/* 所有分享链接弹窗 */}
      {showAllShareLinksDialog && (
        <AllShareLinksDialog
          onClose={() => setShowAllShareLinksDialog(false)}
        />
      )}

      {/* 项目上传对话框 */}
      {showProjectUploadDialog && (() => {
        const { data } = useDataStore.getState()
        let currentProject = data.projects.find(p => p.id === data.currentProjectId)
        
        // 兜底逻辑：如果 Store 中找不到，尝试从原生获取
        if (!currentProject) {
           console.warn('⚠️ [App] Store中未找到当前项目，尝试从原生环境获取')
           const win = window as any
           if (typeof win.getCurrentProject === 'function') {
               const legacyProject = win.getCurrentProject()
               if (legacyProject) {
                   console.log('✅ [App] 从原生环境获取到项目:', legacyProject.name)
                   // 构造兼容的项目对象
                   currentProject = {
                       ...legacyProject,
                       uuid: legacyProject.uuid || legacyProject.id
                   }
               }
           }
        }
        
        if (!currentProject) {
          console.error('❌ [App] 无法获取当前项目，无法显示上传对话框')
          return null
        }
        
        console.log('🚀 [App] 渲染 ProjectUploadDialog, 项目:', currentProject.name)

        return (
          <ProjectUploadDialog
            projectId={currentProject.id || currentProject.uuid}
            projectName={currentProject.name}
            currentCoverImage={currentProject.coverImage}
            currentDescription={currentProject.description}
            onClose={() => setShowProjectUploadDialog(false)}
            onConfirm={async (uploadData) => {
              const win = window as any
              const { updateProject } = useDataStore.getState()
              
              // 更新项目数据（不同步到服务器，因为后面会单独推送）
              updateProject(currentProject.id || currentProject.uuid, {
                name: uploadData.name,
                description: uploadData.description,
                coverImage: uploadData.coverImage || undefined,
                coverThumbnailFileId: uploadData.coverThumbnailFileId || undefined
              }, false)

              // 同时更新 legacy-script 中的数据
              const legacyProject = win.getCurrentProject()
              if (legacyProject) {
                legacyProject.name = uploadData.name
                legacyProject.description = uploadData.description
                legacyProject.coverImage = uploadData.coverImage
                if (uploadData.coverThumbnailFileId) {
                  legacyProject.coverThumbnailFileId = uploadData.coverThumbnailFileId
                }
              }

              // 保存数据到本地 IndexedDB（使用 legacy 版本，不会同步服务器）
              if (typeof win.saveData === 'function') {
                await win.saveData()
              }

              // 使用 legacy-script 的 pushProjectToServer（包含媒体上传逻辑）
              if (typeof win.pushProjectToServer === 'function') {
                await win.pushProjectToServer(currentProject.id || currentProject.uuid)
              } else {
                // 降级方案：使用 store 的方法（不包含媒体上传）
                console.warn('⚠️ legacy pushProjectToServer 不可用，使用降级方案')
                const { pushProjectToServer } = useDataStore.getState()
                await pushProjectToServer(currentProject.id || currentProject.uuid)
              }
            }}
          />
        )
      })()}

      {/* 链接设置弹窗 */}
      {linkDialogState.visible && (
        <LinkDialog
          initialUrl={linkDialogState.initialUrl}
          onClose={() => setLinkDialogState({ ...linkDialogState, visible: false })}
          onSave={async (shareLink: string) => {
            const win = window as any
            const itemIndex = linkDialogState.itemIndex
            
            console.log('[LinkDialog onSave] 开始保存，itemIndex:', itemIndex, 'shareLink:', shareLink)
            
            // 参考图片弹窗的 handleShareLinkChange 实现
            if (typeof win.getCurrentProject !== 'function') {
              console.error('[LinkDialog onSave] getCurrentProject函数不存在')
              setLinkDialogState({ ...linkDialogState, visible: false })
              return
            }
            
            const currentProject = win.getCurrentProject()
            if (!currentProject || !currentProject.items || itemIndex < 0) {
              console.error('[LinkDialog onSave] 项目不存在或itemIndex无效')
              setLinkDialogState({ ...linkDialogState, visible: false })
              return
            }
            
            const item = currentProject.items[itemIndex]
            if (!item) {
              console.error('[LinkDialog onSave] item不存在')
              setLinkDialogState({ ...linkDialogState, visible: false })
              return
            }
            
            // 更新分享链接（参考图片弹窗的做法）
            item.shareLink = shareLink || undefined
            item.updatedAt = new Date().toISOString()
            
            console.log('[LinkDialog onSave] 已更新item shareLink')
            
            // 更新画布显示
            if (typeof win.renderCanvas === 'function') {
              win.renderCanvas()
            }
            
            // 保存到IndexedDB
            if (typeof win.saveData === 'function') {
              try {
                await win.saveData()
                console.log('[LinkDialog onSave] 数据已保存到IndexedDB')
              } catch (e) {
                console.error('[LinkDialog onSave] 保存到IndexedDB失败:', e)
              }
            }
            
            // 更新本地状态
            setCurrentProjectItems([...currentProject.items])
            
            setLinkDialogState({ ...linkDialogState, visible: false })
          }}
        />
      )}

      {/* 图片右键菜单 */}
      {contextMenuState.visible && (
        <ImageContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          itemIndex={contextMenuState.itemIndex}
          onClose={() => setContextMenuState({ visible: false, itemIndex: -1, x: 0, y: 0 })}
          onView={async () => {
            const win = window as any
            if (typeof win.openItemViewer === 'function') {
              win.openItemViewer(contextMenuState.itemIndex)
            }
          }}
          onDownload={async () => {
            const win = window as any
            if (typeof win.downloadImage === 'function') {
              await win.downloadImage(contextMenuState.itemIndex)
            }
          }}
          onSendToBack={async () => {
            const win = window as any
            if (typeof win.sendItemToBack === 'function') {
              await win.sendItemToBack(contextMenuState.itemIndex)
              // 更新本地状态
              if (typeof win.getCurrentProject === 'function') {
                const project = win.getCurrentProject()
                if (project && project.items) {
                  setCurrentProjectItems([...project.items])
                }
              }
            }
          }}
          onSendToFront={async () => {
            const win = window as any
            if (typeof win.sendItemToFront === 'function') {
              await win.sendItemToFront(contextMenuState.itemIndex)
              // 更新本地状态
              if (typeof win.getCurrentProject === 'function') {
                const project = win.getCurrentProject()
                if (project && project.items) {
                  setCurrentProjectItems([...project.items])
                }
              }
            }
          }}
          onRotate={async () => {
            const win = window as any
            if (typeof win.rotateItem === 'function') {
              await win.rotateItem(contextMenuState.itemIndex, 90)
              // 更新本地状态
              if (typeof win.getCurrentProject === 'function') {
                const project = win.getCurrentProject()
                if (project && project.items) {
                  setCurrentProjectItems([...project.items])
                }
              }
            }
          }}
          onLink={() => {
              // 获取当前分享链接（参考图片弹窗的做法）
              const win = window as any
              let initialUrl = ''
              if (win.getCurrentProject) {
                  const project = win.getCurrentProject()
                  if (project && project.items && project.items[contextMenuState.itemIndex]) {
                      initialUrl = project.items[contextMenuState.itemIndex].shareLink || ''
                  }
              }
              setLinkDialogState({
                  visible: true,
                  itemIndex: contextMenuState.itemIndex,
                  initialUrl: initialUrl
              })
          }}
          onSetAsCover={async () => {
            const win = window as any
            const { updateProject, saveData, pushProjectToServer, userUuid } = useDataStore.getState()
            
            if (typeof win.getCurrentProject === 'function') {
              const project = win.getCurrentProject()
              if (project && project.items && project.items[contextMenuState.itemIndex]) {
                const item = project.items[contextMenuState.itemIndex]
                if (item.type === 'image') {
                  // 获取图片的 fileId
                  const fileId = item.fileId || item.imageId
                  const thumbnailFileId = item.thumbnailFileId
                  
                  if (fileId) {
                    try {
                      // 更新项目的封面
                      const projectId = project.id || project.uuid
                      updateProject(projectId, {
                        coverImage: fileId,
                        coverThumbnailFileId: thumbnailFileId || undefined,
                        updatedAt: Date.now()
                      })
                      
                      // 同步到原生代码
                      if (typeof win.syncDataFromReact === 'function') {
                        const latestData = useDataStore.getState().data
                        win.syncDataFromReact(latestData)
                      }
                      
                      // 更新原生代码中的项目封面
                      if (project) {
                        project.coverImage = fileId
                        if (thumbnailFileId) {
                          project.coverThumbnailFileId = thumbnailFileId
                        }
                      }
                      
                      // 先保存到本地数据库
                      await saveData()
                      
                      // 推送项目到服务器（确保封面数据已上传）
                      if (projectId) {
                        try {
                          await pushProjectToServer(projectId)
                          console.log('✅ 项目封面已上传到服务器')
                          
                          // 等待一下，确保服务器数据已更新
                          await new Promise(resolve => setTimeout(resolve, 300))
                          
                          // 获取项目的 UUID（用于查询分享链接）
                          const projectUuid = project.uuid || projectId
                          
                          // 更新该项目对应的所有分享链接的封面
                          if (userUuid && projectUuid) {
                            try {
                              // 获取项目的所有分享链接（使用 projectUuid）
                              const shareListResponse = await fetch(`/api/share/list?userUuid=${userUuid}&projectUuid=${projectUuid}`)
                              if (shareListResponse.ok) {
                                const shareListData = await shareListResponse.json()
                                if (shareListData.success && shareListData.data && shareListData.data.length > 0) {
                                  // 更新每个分享链接的封面
                                  const updatePromises = shareListData.data.map((shareLink: any) => 
                                    fetch(`/api/share/${shareLink.shareId}/cover`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        coverImageFileId: fileId,
                                        coverThumbnailFileId: thumbnailFileId || null
                                      })
                                    })
                                  )
                                  await Promise.all(updatePromises)
                                  console.log(`✅ 已更新 ${shareListData.data.length} 个分享链接的封面`)
                                }
                              }
                            } catch (shareError) {
                              console.error('❌ 更新分享链接封面失败:', shareError)
                              // 不影响主流程，继续执行
                            }
                          }
                        } catch (pushError) {
                          console.error('❌ 上传项目到服务器失败:', pushError)
                          // 即使上传失败，也继续执行，至少本地已更新
                        }
                      }
                      
                      console.log('✅ 封面已设置为图片:', fileId)
                      
                      // 触发刷新封面事件（通知 WorksView 刷新）
                      window.dispatchEvent(new CustomEvent('coverUpdated', {
                        detail: { projectId: projectId }
                      }))
                    } catch (error) {
                      console.error('❌ 设置封面失败:', error)
                    }
                  } else {
                    console.warn('⚠️ 图片没有 fileId 或 imageId，无法设置为封面')
                  }
                }
              }
            }
          }}
        />
      )}
        </div>
      </div>

      {/* 其他视图 */}
      {activeTab === 'marketplace' && <MarketplaceView />}
      {activeTab === 'works' && <WorksView key={userUuid || 'no-user'} onOpenProject={handleOpenProject} />}
      {activeTab === 'library' && <LibraryView key={userUuid || 'no-user'} />}
      {activeTab === 'share' && <SharesManagerView />}
      {activeTab === 'help' && <HelpView />}
      {activeTab === 'settings' && <SettingsView />}
      {activeTab === 'mine' && <UserView />}
      {activeTab === 'intro' && <SystemIntroView />}

      {/* 关闭项目确认对话框 */}
      <ConfirmDialog
        visible={closeProjectConfirm.visible}
        title="关闭画布"
        message={`画布「${closeProjectConfirm.projectName}」有未保存的更改。\n是否保存后再关闭？`}
        confirmText="保存并关闭"
        cancelText="取消"
        thirdButtonText="不保存"
        onConfirm={async () => {
          const win = window as any
          if (typeof win.saveProjectAndClose === 'function') {
            await win.saveProjectAndClose(closeProjectConfirm.projectId)
          }
          setCloseProjectConfirm({ visible: false, projectId: '', projectName: '' })
        }}
        onCancel={() => {
          setCloseProjectConfirm({ visible: false, projectId: '', projectName: '' })
        }}
        onThirdButton={() => {
          const win = window as any
          if (typeof win.doCloseProject === 'function') {
            win.doCloseProject(closeProjectConfirm.projectId)
          }
          setCloseProjectConfirm({ visible: false, projectId: '', projectName: '' })
        }}
      />
    </Layout>
  )
}

// 主应用包装组件，处理初始化逻辑
const MainAppWrapper = () => {
  const [isInitialized, setIsInitialized] = useState<boolean | null>(null)
  const { setUserUuid } = useDataStore()

  useEffect(() => {
    checkInitialization()
  }, [])

  const checkInitialization = async () => {
    try {
      // 检查公共数据库是否存在
      const mainDBExists = await checkMainDBExists()
      
      if (!mainDBExists) {
        // 数据库不存在，需要初始化
        setIsInitialized(false)
        return
      }

      // 检查当前用户
      const currentUserUuid = await getCurrentUserUuid()
      
      if (!currentUserUuid) {
        // 没有当前用户，需要初始化
        setIsInitialized(false)
        return
      }

      // 检查用户数据库是否存在
      const userDBExists = await checkUserDBExists(currentUserUuid)
      
      if (!userDBExists) {
        // 用户数据库不存在，需要初始化
        setIsInitialized(false)
        return
      }

      // 一切正常，已初始化
      setIsInitialized(true)
    } catch (err) {
      console.error('检查初始化状态失败:', err)
      // 出错时也显示初始化界面
      setIsInitialized(false)
    }
  }

  const handleInitComplete = async (userUuid: string) => {
    // 设置用户 UUID
    await setUserUuid(userUuid)
    // 标记为已初始化
    setIsInitialized(true)
  }

  // 正在检查初始化状态
  if (isInitialized === null) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-300 border-t-pink-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">正在加载...</p>
        </div>
      </div>
    )
  }

  // 需要初始化
  if (!isInitialized) {
    return <InitializationView onComplete={handleInitComplete} />
  }

  // 已初始化，显示主应用
  return <MainApp />
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/share/:shareId" element={<ShareView />} />
        <Route path="*" element={<MainAppWrapper />} />
      </Routes>
    </BrowserRouter>
  )
}

// 图片URL加载器组件（用于异步从IndexedDB加载图片，统一使用fileId和STORE_MEDIA）
const ImageUrlLoader = ({ item, onLoad }: { item: any; onLoad: (url: string) => JSX.Element }) => {
  const [imageUrl, setImageUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadImageUrl = async () => {
      try {
        let url = ''
        
        // 统一使用fileId，兼容旧数据的imageId
        const fileId = item.fileId || item.imageId
        if (fileId) {
          // 尝试从 IndexedDB 获取图片（使用统一的media表）
          const win = window as any
          if (typeof win.openDB === 'function') {
            try {
              const db = await win.openDB()
              const STORE_MEDIA = 'media' // 统一使用media表
              const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
              
              // 如果fileId包含扩展名，提取原始UUID
              let originalFileId = fileId
              if (fileId.includes('.')) {
                originalFileId = fileId.substring(0, fileId.lastIndexOf('.'))
              }
              
              const result = await new Promise((resolve, reject) => {
                const request = mediaStore.get(originalFileId)
                request.onsuccess = () => resolve(request.result)
                request.onerror = () => reject(request.error)
              })
              if (result && (result as any).blob) {
                url = URL.createObjectURL((result as any).blob)
              }
            } catch (e) {
              console.error('从IndexedDB加载图片失败:', e)
            }
          }
          
          // 如果IndexedDB加载失败，尝试使用imageSrc
          if (!url && item.imageSrc) {
            url = item.imageSrc
          }
        } else if (item.imageSrc) {
          url = item.imageSrc
        }
        
        setImageUrl(url)
      } catch (error) {
        console.error('加载图片URL失败:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadImageUrl()
  }, [item])

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center">
        <div className="text-white">加载中...</div>
      </div>
    )
  }

  if (!imageUrl) {
    return null
  }

  return onLoad(imageUrl)
}

// 文件URL加载器组件（用于异步从IndexedDB加载文件）
const FileUrlLoader = ({ item, onLoad }: { item: any; onLoad: (url: string) => JSX.Element }) => {
  const [fileUrl, setFileUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadFileUrl = async () => {
      try {
        let url = ''
        
        if (item.fileId) {
          // 尝试从 IndexedDB 获取文件（使用统一的media表）
          const win = window as any
          if (typeof win.openDB === 'function') {
            try {
              const db = await win.openDB()
              const STORE_MEDIA = 'media' // 统一使用media表
              const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
              
              // 如果fileId包含扩展名，提取原始UUID
              let originalFileId = item.fileId
              if (item.fileId.includes('.')) {
                originalFileId = item.fileId.substring(0, item.fileId.lastIndexOf('.'))
              }
              
              const result = await new Promise((resolve, reject) => {
                const request = mediaStore.get(originalFileId)
                request.onsuccess = () => resolve(request.result)
                request.onerror = () => reject(request.error)
              })
              if (result && (result as any).blob) {
                url = URL.createObjectURL((result as any).blob)
              }
            } catch (e) {
              console.error('从IndexedDB加载文件失败:', e)
            }
          }
        }
        
        setFileUrl(url)
      } catch (error) {
        console.error('加载文件URL失败:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFileUrl()
  }, [item])

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center">
        <div className="text-white">加载中...</div>
      </div>
    )
  }

  if (!fileUrl) {
    return null
  }

  return onLoad(fileUrl)
}

export default App
